import { z } from "zod"

// Step 1: Destination Details
export const invoiceSchema = z.object({
  invoiceFile: z.instanceof(File).optional(),
})

export const destinationSchema = z.object({
  // A sale needs a purpose (the catalogue's purpose id) and an encashment
  // needs neither, so the page asks for them by mode, not here.
  purposeOfTravel: z.string().optional(),
  destination: z.string().optional(),
  dateRange: z.object({
    start_date: z.string().optional(),
    end_date: z.string().optional(),
  }).refine((data) => {
    if (!data.start_date || !data.end_date) return true;
    const startDate = new Date(data.start_date);
    const endDate = new Date(data.end_date);
    return startDate <= endDate;
  }, {
    message: "End date can not be earlier than start date",
    path: ["end_date"],
  }),
});


// Product Schema
export const currencySchema = z.object({
  currency: z.object({
    value: z.string(),
    label: z.string(),
  }),
  amount: z.number().positive("Amount must be positive"),
  rate: z.number().optional(),
  // The rate core quoted, and why the operator changed it when they did.
  liveRate: z.number().optional(),
  rateReason: z.string().optional(),
})

export const productSchema = z.object({
  productType: z.object({
    value: z.string(),
    label: z.string(),
    type: z.string().optional(),
  }),
  product: z.object({
    value: z.string(),
    label: z.string(),
    type: z.string().optional(),
  }).optional(),
  cardType: z
    .object({
      value: z.string(),
      label: z.string(),
    })
    .optional(),
  currencies: z.array(currencySchema).min(1, "At least one currency is required"),
  local_id: z.string()
})

// Step 2: Customer Details
export const customerSchema = z.object({
  customerId: z.string().optional(),
  mobileNumber: z.string().min(10, "Mobile number must be at least 10 digits"),
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  pan: z.string().optional(),
  products: z.array(productSchema).optional(),
})

// Step 3: Delivery Details
export const addressSchema = z.object({
  address1: z.string().optional(),
  address2: z.string().optional(),
  landmark: z.string().optional(),
  country: z.object({
    value: z.string(),
    label: z.string(),
  }),
  state: z.object({
    value: z.string(),
    label: z.string(),
  }),
  city: z.string().optional(),
  /** The catalogue's id for the city, which the delivery charge is worked out from. */
  cityId: z.string().optional(),
  pincode: z.string().optional(),

})

export const deliverySchema = z
  .object({
    deliveryMode: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .refine((data) => data.value.trim() !== "", {
        message: "Delivery mode is required",
      }),
    deliveryId: z.string().optional(),
    trackingId: z.string().optional(),
    products: z
      .array(
        z.object({
          value: z.string(),
          label: z.string(),
        })
      )
      .min(1, "At least one product is required"),
    customers: z
      .object({
        value: z.string(),
        label: z.string(),
      })
      .refine((data) => data.value.trim() !== "", {
        message: "Customer ID is required",
      }),
    deliveryType: z.enum(["self", "courier", "pickup"]).optional(),
    locationInfo: z.string().optional(),
    addresses: z.array(addressSchema).optional(), // <-- optional by default
    local_id: z.array(z.string()).optional(),
    address_id: z.string().optional(), // <-- optional by default
    date: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    // Only validate addresses & address_id when deliveryMode is NOT pickup
    if (data.deliveryMode?.value !== "pickup") {
      if (!data.addresses || data.addresses.length === 0) {
        ctx.addIssue({
          path: ["addresses"],
          message: "At least one address is required",
          code: z.ZodIssueCode.custom,
        });
      }
      // if (!data.address_id || data.address_id.trim() === "") {
      //   ctx.addIssue({
      //     path: ["address_id"],
      //     message: "Address ID is required",
      //     code: z.ZodIssueCode.custom,
      //   });
      // }
    }
  });

export const kycSchema = z.object({
  // make it required
  pan: z.instanceof(File).optional(),
  passportFront: z.instanceof(File).optional(),
  passportBack: z.instanceof(File).optional(),
})

// What core needs on a forex order beyond its lines. Choices are held as the
// text the selects give; the page turns them into booleans and numbers.
export const declarationsSchema = z.object({
  residency: z.string().optional(),
  payerKind: z.string().optional(),
  payerOrganizationId: z.string().optional(),
  payerName: z.string().optional(),
  travellerDifferent: z.string().optional(),
  travellerCustomerId: z.string().optional(),
  lrsDeclaredUsd: z.union([z.string(), z.number()]).optional(),
  lrsSigned: z.string().optional(),
  employerBorne: z.string().optional(),
  sponsorEvidenceDocumentId: z.string().optional(),
  paymentMode: z.string().optional(),
  instrumentRef: z.string().optional(),
  cashAmount: z.union([z.string(), z.number()]).optional(),
  declaredOnArrival: z.string().optional(),
  certificateRequested: z.string().optional(),
  payoutMethod: z.string().optional(),
})

// Complete Form Schema
export const travelBookingSchema = z.object({
  invoice: invoiceSchema.optional(),
  destination: destinationSchema,
  customers: z.array(customerSchema).min(1, "At least one customer is required"),
  deliveries: z.preprocess((val) => val ?? [], z.array(deliverySchema).optional()),
  order_type: z.enum(["buy", 'sell']).optional(),
  kyc: kycSchema.optional(),
  declarations: declarationsSchema.optional(),
})

export type DestinationFormValues = z.infer<typeof destinationSchema>
export type CustomerFormValues = z.infer<typeof customerSchema>
export type ProductFormValues = z.infer<typeof productSchema>
export type DeliveryFormValues = z.infer<typeof deliverySchema>
export type TravelBookingFormValues = z.infer<typeof travelBookingSchema>
export type InvoiceFormValues = z.infer<typeof invoiceSchema>
export type DeclarationsFormValues = z.infer<typeof declarationsSchema>
export type KYCFormValues = z.infer<typeof kycSchema>
export type ProductValues = z.infer<typeof productSchema>